import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { prepareHeaders, buildError, API } from '../../api/apis';
import { ApiResponse, DescribeNetworkMonitoringResponse, DescribeTenantSettingsRequest, DescribeTenantSettingsResponse, FeatureFlagsTypeEnum, IPAMPool, UpdateNetworkMonitoringRequest, UpdateTenantSettingsRequest, UpdateTenantSettingsResponse } from '../../interfaces/interfaces';

// Define the RTK Query service for settings
export const settingsService = createApi({
  reducerPath: 'settingsApi',
  tagTypes: ['Settings', 'FeatureFlags', 'NetworkMonitoring'],
  baseQuery: fetchBaseQuery({
    prepareHeaders: prepareHeaders,
  }),
  endpoints: (builder) => ({
    getFeatureFlags: builder.query({
      query: (requestSettings: DescribeTenantSettingsRequest) => {
        const result = API.FETCH_SETTINGS(requestSettings);
        return result || ''; // Ensure a valid string is returned
      },
      transformResponse: (response: ApiResponse) => {
        return response.Response?.FeatureFlags as Record<FeatureFlagsTypeEnum, boolean>[] || [];
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
      providesTags: () => [{ type: 'FeatureFlags', id: 'DESCRIBE' }],
    }),
    getTenantSettings: builder.query({
      query: (requestSettings: DescribeTenantSettingsRequest | undefined) => {
        const result = API.FETCH_SETTINGS(requestSettings);
        return result || ''; // Ensure a valid string is returned
      },
      providesTags: () => [{ type: 'Settings', id: 'DESCRIBE' }],
      transformResponse: (response: ApiResponse) => {
        return response.Response as DescribeTenantSettingsResponse;
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
    }),
    getBYOIPSettings: builder.query<
      { PublicIPPools: Record<string, IPAMPool> },
      { region: string }
    >({
      query: ({ region }) => {
        return API.BYOIP_SETTINGS(region);
      },
      providesTags: () => [{ type: 'Settings', id: 'BYOIP' }],
      transformResponse: (response: ApiResponse) => {
        return response.Response as { PublicIPPools: Record<string, IPAMPool> };
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
    }),
    updateTenantSettings: builder.mutation({
      query: (requestUpdateSettings: UpdateTenantSettingsRequest) => ({
        url: API.UPDATE_SETTINGS(requestUpdateSettings),
        method: import.meta.env.VITE_MICROMGMT === 'true' ? 'PATCH' : 'PUT',
        body: requestUpdateSettings,
      }),
      invalidatesTags: [{ type: 'Settings', id: 'DESCRIBE' }],
      transformResponse: (response: ApiResponse) => {
        return response.Response as UpdateTenantSettingsResponse;
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
    }),
    getNetworkMonitoring: builder.query({
      query: (request: { Region: string }) => API.FETCH_SETTINGS({ networkmonitoring: 'true', Region: request.Region }),
      providesTags: () => [{ type: 'NetworkMonitoring', id: 'DESCRIBE' }],
      transformResponse: (response: ApiResponse) => {
        return response.Response as DescribeNetworkMonitoringResponse;
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
    }),
    updateNetworkMonitoring: builder.mutation({
      query: (request: UpdateNetworkMonitoringRequest) => ({
        url: API.UPDATE_SETTINGS(),
        method: 'PATCH',
        body: { EnableNetworkMonitoring: request.EnableNetworkMonitoring },
        params: { region: request.Region },
      }),
      invalidatesTags: [{ type: 'NetworkMonitoring', id: 'DESCRIBE' }],
      transformResponse: (response: ApiResponse) => {
        return response.Response as DescribeNetworkMonitoringResponse;
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
    }),
    syncIPAMPool: builder.mutation<
      { PublicIPPools: Record<string, IPAMPool> },
      { ipamPoolId: string; region: string }
    >({
      query: ({ ipamPoolId, region }) => ({
        url: API.BYOIP_SETTINGS(region),
        method: 'PATCH',
        body: {
          PublicIPPools: {
            [ipamPoolId]: 'sync',
          },
        },
      }),
      invalidatesTags: [{ type: 'Settings', id: 'BYOIP' }],
      transformResponse: (response: ApiResponse) => {
        return response.Response as { PublicIPPools: Record<string, IPAMPool> };
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
    }),
  }),
});

// Export hooks for usage in functional components
export const { useGetFeatureFlagsQuery, useGetTenantSettingsQuery, useGetBYOIPSettingsQuery, useUpdateTenantSettingsMutation, useGetNetworkMonitoringQuery, useLazyGetNetworkMonitoringQuery, useUpdateNetworkMonitoringMutation, useSyncIPAMPoolMutation } = settingsService;
